<script setup lang="ts">
import CoreButton from 'frontcore/components/CoreButton.vue'
import i18n from 'frontcore/i18n'
import RemoteFiltersItem from 'frontcore/presentation/remote-filters/views/RemoteFiltersItem.vue'
import RemoteFiltersSearch from 'frontcore/presentation/remote-filters/views/RemoteFiltersSearch.vue'
import RemoteFiltersSelectedChips from 'frontcore/presentation/remote-filters/views/RemoteFiltersSelectedChips.vue'
import SidebarModal from 'frontcore/views/modals/SidebarModal/SidebarModal.vue'
import useRemoteFiltersStore from 'frontcore/presentation/remote-filters/data/use-remote-filters-store'
import { computed, onUnmounted, ref, watch } from 'vue'
import { eventBus } from 'frontcore/communications/eventBus'
import { PORTAL_LEVELS } from 'frontcore/constants/constants'
import {
  RemoteFiltersFieldType,
  RemoteFiltersStoreConfiguration,
} from 'frontcore/presentation/remote-filters/domain/types'
import { useRoute, useRouter } from 'vue-router'

type Props = {
  filterURL: string
  storeConfiguration?: RemoteFiltersStoreConfiguration
}
const props = defineProps<Props>()

type Emits = (e: 'didLoad') => void
const emit = defineEmits<Emits>()

const route = useRoute()
const router = useRouter()

const isLoading = ref(true)

const filtersStore = computed(() => {
  return useRemoteFiltersStore(props.filterURL, props.storeConfiguration, route, router)
})

watch(
  () => route.name,
  () => {
    (async () => {
      isLoading.value = true
      await filtersStore.value.initialFetch()
      emit('didLoad')
      isLoading.value = false
    })()
  },
  { immediate: true }
)

onUnmounted(() => {
  eventBus.off('openRemoteFilters')
})

const isSideBarFilterVisible = ref(false)

const toggleSideBarVisibility = () => {
  if (isLoading.value) { return }
  setTimeout(() => {
    isSideBarFilterVisible.value = !isSideBarFilterVisible.value
  })
}
eventBus.on('openRemoteFilters', toggleSideBarVisibility)

const closeSideBar = () => {
  filtersStore.value.clearChanges()
  toggleSideBarVisibility()
}

const filterConfigs = computed(() => {
  return filtersStore.value.moduleRemoteFiltersConfigurations.filter(filter => {
    const hasRelatedFilter = filtersStore.value.moduleRemoteFiltersConfigurations.some(availableFilters => filter.relatedFilters.includes(availableFilters.filterId))
    return filter.filterType !== RemoteFiltersFieldType.SEARCH && !hasRelatedFilter
  })
})

const { t } = i18n.global

const header = {
  dataTest: 'remote-filters-header',
  iconName: 'FilterAlt',
  onCloseHandler: toggleSideBarVisibility,
  title: t('common.messages.filters'),
}

const viewResults = () => {
  toggleSideBarVisibility()
}

const resetFilters = () => {
  filtersStore.value.clearChanges()
  toggleSideBarVisibility()
}
</script>

<template>
  <div class="remote-filters s-my-4">
    <div class="remote-filters__bar flex flex-column gap-4">
      <div
        class="remote-filters__header flex w-50 gap-4"
        data-test="remote-filters-header">
        <RemoteFiltersSearch
          :filterURL="filterURL"
          :storeConfiguration="storeConfiguration"
          class="flex-grow-1" />
        <CoreButton
          buttonType="secondary"
          data-test="available-filters-button"
          iconName="FilterAlt"
          :disabled="isLoading"
          :label="t('common.messages.filters')"
          @click="toggleSideBarVisibility" />
      </div>
      <RemoteFiltersSelectedChips
        v-if="filtersStore.remoteFilterStoreID"
        :filterStoreID="filtersStore.remoteFilterStoreID"
        @selectFilterItem="toggleSideBarVisibility" />
    </div>
    <SidebarModal
      v-if="isSideBarFilterVisible"
      :portalTarget="PORTAL_LEVELS.THREE"
      :header="header"
      :closeButtonLabel="t('common.close')"
      dataTest="sidebar-modal-filters"
      modalClass="col-start-1 col-span-12 sm:col-start-9 sm:col-span-4 xxl:col-start-10 xxl:col-span-3"
      @close="closeSideBar">
      <template #body>
        <div class="flex flex-col gap-2">
          <div class="gap-8">
            <div class="gap-4 flex flex-column">
              <RemoteFiltersItem
                v-for="configuration in filterConfigs"
                :key="configuration.filterId"
                :configuration="configuration"
                :moduleRemoteFiltersConfigurations="filtersStore.moduleRemoteFiltersConfigurations"
                :filterStoreID="filtersStore.remoteFilterStoreID" />
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <CoreButton
          dataTest="remote-filter-commit-button"
          class="tw-grow"
          :label="t('common.messages.see_results')"
          @click="viewResults" />
        <CoreButton
          buttonType="destructive-ghost"
          class="tw-grow"
          iconName="RefreshFill"
          dataTest="remote-filter-reset-button"
          :label="t('common.messages.reset')"
          @click="resetFilters" />
      </template>
    </SidebarModal>
  </div>
</template>
